import type { NavigateFunction } from 'react-router-dom'
import type { AppDispatch } from '@/redux/store'
import type { SearchResultItem, XMetaDataItem } from '../../search/types'
import { setMediaDetailsState } from '../slice'
import { PCE_PAX_IDS } from './constants'
import type { TableMapModel, UserSelectors, XMetaSelector } from './types'

const getAffiliationsValue = (baseValue: unknown): string => {
  const combine = (item: unknown) => {
    if (!item || typeof item !== 'object') return ''

    const obj = item as Record<string, unknown>
    const code3 = obj['code3'] as string | undefined
    const desc = obj['description'] as string | undefined

    if (code3 && desc) return `${code3} - ${desc}`
    return (code3 ?? desc ?? '') as string
  }

  if (Array.isArray(baseValue)) {
    return baseValue
      .map((v) => combine(v))
      .filter((v) => v !== null && v !== '')
      .sort((a, b) => a.localeCompare(b))
      .join(', ')
  }

  if (typeof baseValue === 'object') {
    return combine(baseValue)
  }

  return ''
}

const formatValue = (value: unknown, mapItem?: TableMapModel): string => {
  if (value === null) return ''

  if (Array.isArray(value)) {
    return value.map((v) => formatValue(v, mapItem)).join(', ')
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  const transform = mapItem?.transform

  if (!transform) return String(value)

  switch (transform.type) {
    case 'lookup':
      return typeof value === 'string'
        ? (transform.map[value] ?? value)
        : String(value)
    case 'date': {
      const date = new Date(value as string | number | Date)
      return Number.isNaN(date.getTime())
        ? ''
        : new Intl.DateTimeFormat('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          }).format(date)
    }
    default:
      return ''
  }
}

const getXMetaSingle = (items: XMetaDataItem[], valueCode: string) => {
  const item = items.find(
    (i) => i.valueCode.toUpperCase() === valueCode.toUpperCase(),
  )
  if (!item || item.freeTextValue.length === 0) {
    return ''
  }
  return item.freeTextValue.length === 1
    ? item.freeTextValue[0]
    : item.freeTextValue.join(', ')
}

const getXMetaStartsWith = (items: XMetaDataItem[], valueCode: string) =>
  items
    .filter((i) =>
      i.valueCode.toUpperCase().startsWith(valueCode.toUpperCase()),
    )
    .flatMap((i) =>
      i.dataType === 'FREETEXTMULTI' ? i.freeTextValue : [i.valueDescription],
    )
    .filter(Boolean)
    .join(', ')

const getXMetaRadio = (items: XMetaDataItem[], valueCode: string) =>
  items.find((i) => i.dataCode.toUpperCase() === valueCode.toUpperCase())
    ?.valueDescription ?? ''

const getXMetaBoolean = (items: XMetaDataItem[], valueCode: string) =>
  items.some(
    (i) =>
      i.valueCode.toUpperCase() === valueCode.toUpperCase() &&
      i.valueDescription.toUpperCase() === 'YES',
  )
    ? 'YES'
    : ''

const getXMetaValue = (baseValue: unknown, xMeta: XMetaSelector) => {
  const items = baseValue as XMetaDataItem[]
  switch (xMeta.kind) {
    case 'single':
      return getXMetaSingle(items, xMeta.valueCode)
    case 'startsWith':
      return getXMetaStartsWith(items, xMeta.valueCode)
    case 'radio':
      return getXMetaRadio(items, xMeta.valueCode)
    case 'boolean':
      return getXMetaBoolean(items, xMeta.valueCode)
    default:
      return ''
  }
}

const getByPath = (obj: unknown, path?: string): unknown => {
  if (!obj || !path) return obj

  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[key]
    }
    return undefined
  }, obj)
}

export const getTableValue = (
  searchResultItem: SearchResultItem,
  mapItem: TableMapModel,
): string => {
  const baseValue = getByPath(searchResultItem, mapItem.keyName)

  if (baseValue === null) return ''

  if (mapItem.keyName === 'xMetadata' && mapItem.xMeta) {
    return getXMetaValue(baseValue, mapItem.xMeta)
  }

  if (!mapItem.valuePath) {
    return formatValue(baseValue, mapItem)
  }

  if (
    mapItem.keyName === 'affiliations' &&
    mapItem.valuePath === 'code3' &&
    baseValue
  ) {
    return getAffiliationsValue(baseValue)
  }

  if (Array.isArray(baseValue)) {
    return baseValue
      .map((v) => getByPath(v, mapItem.valuePath))
      .filter((v) => v !== null && v !== '')
      .map((v) => formatValue(v, mapItem))
      .join(', ')
  }

  if (typeof baseValue === 'object') {
    return formatValue(getByPath(baseValue, mapItem.valuePath), mapItem)
  }

  return ''
}

export const handleReturn = (
  dispatch: AppDispatch,
  navigate: NavigateFunction,
  destination: '/search' | '/mediamanagement' = '/search',
) => {
  navigate(destination, { replace: true })
  dispatch(setMediaDetailsState({ searchResultItem: null }))
}

const getIsDelegate = (
  searchResultItem: SearchResultItem,
  selectors: UserSelectors,
): boolean => {
  return selectors
    .selectUserDelegates()
    .some((delegate) => delegate === searchResultItem.ownerCwsId)
}

const getIsOwner = (
  searchResultItem: SearchResultItem,
  selectors: UserSelectors,
): boolean => {
  return searchResultItem.ownerCwsId === selectors.selectUserData()?.catloginid
}

export const getButtonAccess = (
  searchResultItem: SearchResultItem,
  selectors: UserSelectors,
): { edit: boolean; clone: boolean; copy: boolean; recycle: boolean } => {
  const isPcePax = PCE_PAX_IDS.has(searchResultItem.ownerCwsId)
  const role = selectors.selectUserRole()
  const isAdmin = role === 'admin'
  const isProvider = role === 'provider'
  const buttons = { edit: false, clone: false, copy: false, recycle: false }

  if (isPcePax) {
    return isAdmin || isProvider ? { ...buttons, copy: true } : buttons
  }

  if (isAdmin) {
    return { edit: true, clone: true, copy: true, recycle: true }
  }

  if (isProvider) {
    const isOwner = getIsOwner(searchResultItem, selectors)
    const isDelegate = getIsDelegate(searchResultItem, selectors)
    if (isOwner || isDelegate) {
      return { edit: true, clone: true, copy: true, recycle: true }
    }
    return { ...buttons, clone: true, copy: true }
  }

  return buttons
}
